import { useState } from "react";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { formatIngreso } from "@/lib/leadPeriod";

export function BuyerAssignment({ buyer, agencies, advisors, onAssigned }) {
  const current = advisors.find((a) => a.id === buyer.asesor_id);
  const [agencyId, setAgencyId] = useState(buyer.agencia_id || "");
  const [advisorId, setAdvisorId] = useState("");
  const [saving, setSaving] = useState(false);
  const activeAgencies = agencies.filter((a) => a.agencia_activa !== false);
  const options = advisors.filter((a) => a.agency_id === agencyId && a.activo && a.id !== buyer.asesor_id);
  const name = (id, list) => list.find((x) => x.id === id)?.nombre || (id ? "—" : "Sin asignar");

  async function save(asesor_id) {
    setSaving(true);
    try {
      const { data } = await api.patch(`/buyers/${buyer.id}/assign`, { asesor_id });
      toast.success(asesor_id ? "Asesor asignado" : "Asignación retirada");
      setAdvisorId("");
      onAssigned(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <div className="mt-4 rounded-sm border border-[#3E5641]/10 p-3" data-testid="buyer-assignment">
      <p className="mono text-[10px] uppercase tracking-widest text-[#A0553F]">Asignación de asesor</p>
      <p className="mt-1 text-sm" data-testid="buyer-assignment-current">
        {current ? <>{current.nombre} · {name(current.agency_id, agencies)}{!current.activo && " (inactivo)"}</> : "Sin asesor asignado"}
      </p>
      <div className="mt-3 grid gap-3 md:grid-cols-[1fr_1fr_auto] md:items-end">
        <div>
          <Label className="field-label">Agencia</Label>
          <Select value={agencyId || undefined} onValueChange={(v) => { setAgencyId(v); setAdvisorId(""); }}>
            <SelectTrigger data-testid="assign-agency"><SelectValue placeholder="Elige agencia" /></SelectTrigger>
            <SelectContent>{activeAgencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.nombre}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="field-label">Asesor activo</Label>
          <Select value={advisorId || undefined} onValueChange={setAdvisorId} disabled={!agencyId}>
            <SelectTrigger data-testid="assign-advisor"><SelectValue placeholder={options.length ? "Elige asesor" : "Sin asesores activos"} /></SelectTrigger>
            <SelectContent>{options.map((a) => <SelectItem key={a.id} value={a.id}>{a.nombre}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <button type="button" disabled={!advisorId || saving} onClick={() => save(advisorId)} className="btn-primary disabled:opacity-50" data-testid="assign-save">
          {buyer.asesor_id ? "Reasignar" : "Asignar"}
        </button>
      </div>
      {buyer.asesor_id && (
        <button type="button" disabled={saving} onClick={() => window.confirm("¿Quitar el asesor asignado?") && save(null)} className="mt-2 text-xs text-red-600 underline" data-testid="assign-remove">Quitar asignación</button>
      )}
      {(buyer.asignacion_historial || []).length > 0 && (
        <div className="mt-3 border-t border-[#3E5641]/10 pt-2" data-testid="assign-history">
          <p className="text-xs text-[#6B7678]">Historial de asignación</p>
          <ul className="mt-1 space-y-1 text-xs">
            {[...buyer.asignacion_historial].reverse().map((h, i) => (
              <li key={i} data-testid={`assign-history-${i}`}>
                {formatIngreso(h.at)} · {h.by}: {name(h.from_asesor_id, advisors)} → {name(h.to_asesor_id, advisors)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
